/**
 * useMicroRawPayload — lazily fetches a single micro-transaction's raw_payload
 * (backend PR #282). Only runs once a row is selected.
 *
 * The API client (request.ts) collapses HTTP status to SUCCESS/ERROR and only
 * forwards the backend error *message* — not the 413 status or error code. So a
 * "payload too large" refusal is recognised by matching the two messages the
 * backend's raw-payload route emits (link.ts): the size-cap refusal
 * ("…exceeding the …-byte limit") and the unknown-size refusal
 * ("Payload size is unknown; refusing to fetch…").
 */

import { useQuery } from "@tanstack/react-query";
import { useApi } from "@/shared/utils/request";

const PAYLOAD_TOO_LARGE_RE =
	/exceeding the|refusing to fetch|payload size is unknown/i;

export class PayloadTooLargeError extends Error {}

export function useMicroRawPayload(microId: string | null) {
	const { makeApiRequest } = useApi();

	const query = useQuery({
		queryKey: ["internal", "micro-raw-payload", microId] as const,
		enabled: !!microId,
		retry: false, // never retry a deliberate 413 refusal
		queryFn: async ({ signal }) => {
			const response = await makeApiRequest({
				endpoint: `/micro-transactions/${microId}/raw-payload`,
				method: "GET",
				otherOptions: { signal },
			});
			if (response.error) {
				if (PAYLOAD_TOO_LARGE_RE.test(response.error)) {
					throw new PayloadTooLargeError(response.error);
				}
				throw new Error(response.error);
			}
			// A null payload is a valid (empty) result, not an error.
			return (response.data ?? null) as unknown;
		},
	});

	return {
		payload: query.data,
		isLoading: query.isLoading,
		isTooLarge: query.error instanceof PayloadTooLargeError,
		error:
			query.error && !(query.error instanceof PayloadTooLargeError)
				? query.error.message
				: null,
	};
}
